import { captureException } from '@sentry/react';
import { type ErrorComponentProps } from '@tanstack/react-router';
import { useEffect } from 'react';

import { AppLogo } from '@/assets/icons/AppLogo';
import { Button } from '@/components/ui/Button';
import CenteredTile from '@/components/ui/CenteredTile';
import { useErrorReception } from '@/hooks/useErrorReception';
import { logoutUser } from '@/utils/auth';
import { clearLocalStorage } from '@/utils/localStorage';
import { handleMailTo } from '@/utils/platform';

export function ErrorScreen({ error, reset }: ErrorComponentProps) {
  const { title, message, action, actionLabel } = useErrorReception(error);

  const handleOnPress = () => {
    reset();

    void action();
  };

  const handleResetApp = async () => {
    try {
      await logoutUser();
    } catch (logoutError) {
      // Even a failed logout must not stop the reset
      console.error(
        '[ErrorScreen] Logout error (continuing reset):',
        logoutError
      );
    }

    clearLocalStorage();

    sessionStorage.clear();

    // Reload to land on login with a clean slate; otherwise the error screen just stays
    // up, since logout only navigates away when a signed-in route is still matched
    window.location.reload();
  };

  useEffect(() => {
    captureException(error);
  }, [error]);

  return (
    <CenteredTile
      title={title}
      description={message}
      leadingContent={<AppLogo size={32} />}
      content={
        <>
          <Button className="w-full mb-6" type="button" onClick={handleOnPress}>
            {actionLabel}
          </Button>

          <p className="mt-2 text-center text-foreground-muted text-sm">
            Keep having trouble?{' '}
            <button
              type="button"
              className="text-accent font-medium cursor-pointer hover:underline"
              onClick={() => void handleResetApp()}
            >
              Reset the app
            </button>{' '}
            or{' '}
            <button
              type="button"
              className="text-accent font-medium cursor-pointer hover:underline"
              onClick={() => {
                handleMailTo(
                  'support@littlebird.ai',
                  'I need assistance',
                  `I need help with the following issue: ${message}`
                );
              }}
            >
              Contact Support
            </button>
          </p>
        </>
      }
    />
  );
}
